/**
 * Data Transfer Object (DTO) types for integrations.
 * These are the wire format used by the API for both requests and responses.
 */

/**
 * Base DTO enum for integration providers.
 * Represents the wire format used by the backend API.
 * This is the single source of truth for API communication.
 */
export enum IntegrationProviderDTO {
  GOOGLE = 'GOOGLE',
  JIRA = 'JIRA',
  QUICK_BOOKS = 'QUICK_BOOKS',
  TEMPO = 'TEMPO',
  CLOCKIFY = 'CLOCKIFY',
  ZOHO_PEOPLE = 'ZOHO_PEOPLE',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Type alias for integration provider in API requests.
 * Uses the base DTO enum.
 */
export type IntegrationProviderRequest = IntegrationProviderDTO;

/**
 * Type alias for integration provider in API responses.
 * Uses the base DTO enum.
 */
export type IntegrationProviderResponse = IntegrationProviderDTO;

/**
 * Response DTO for a single entity integration link (external project/user/client).
 * Represents an unlinked external entity from an integration provider.
 */
export interface EntityIntegrationLinkShortResponse {
  id: number;
  name: string;
}

/**
 * Response DTO for integration links grouped by provider.
 * Contains all unlinked external entities for a specific integration provider.
 */
export interface IntegrationLinksResponse {
  integration: IntegrationProviderResponse;
  links: EntityIntegrationLinkShortResponse[];
}

/**
 * Response DTO for a connected integration (selected external entity).
 * Represents the currently linked external entity in details view.
 */
export interface ConnectedIntegrationResponse {
  id: number;
  name: string;
}

/**
 * Response DTO for integration info in entity details.
 * Contains the integration provider type and optionally a connected external entity.
 */
export interface IntegrationInfoResponse {
  integrationProviderType: IntegrationProviderResponse;
  connectedIntegration: ConnectedIntegrationResponse | null;
}

/**
 * Response DTO for entity integrations section.
 * Contains array of all integration providers with their connected entities.
 */
export interface IntegrationsResponse {
  integrations: IntegrationInfoResponse[];
}
